<template>
  <PortfolioFormLayout :loading="loading">
    <template #header>
      <h5 class="my-2">{{ isUpdateForm ? 'Update' : 'Create' }} Cost Pool</h5>
    </template>
    <template #body>
      <CostPoolFormPresentational
        v-model:cost-center-identifier="costCenterIdentifier"
        v-model:description="description"
        v-model:estimated-capacity="estimatedCapacity"
        v-model:max-capacity="maxCapacity"
        v-model:name="name"
        v-model:percentage-of-cost-center="percentageOfCostCenter"
        v-model:type="type"
        v-model:unit-description="unitDescription"
        v-model:unit-type="unitType"
        v-model:unit-weight-method="unitWeightMethod"
        :type-options="typeOptions"
        :error-messages="errorMessages"
        :loading="loadingSubmit"
        :server-error="serverError"
        @submit="submit"
      />
    </template>
  </PortfolioFormLayout>
</template>
<script lang="ts" setup>
import {Detail} from '@/api/errorMessage';
import {CostPoolCreateUpdate} from '@/stores/userManagement/userManagement';

const props = defineProps<{
  isUpdateForm: boolean;
  loading: boolean;
  id?: number;
}>();

const router = useRouter();

// Stores
const costPoolsStore = useCostPoolsStore();
const getCostPools = computed(() => costPoolsStore.getCostPools);

const loadingSubmit = ref(false);

// Form data
const costCenterIdentifier = ref('');
const description = ref('');
const estimatedCapacity = ref('');
const maxCapacity = ref('');
const name = ref('');
const percentageOfCostCenter = ref('');
const typeOptions = ref(['direct', 'indirect']);
const type = ref(typeOptions.value[0]);
const unitDescription = ref('');
const unitType = ref('');
const unitWeightMethod = ref('');
const errorMessages = ref(new Map());
const serverError = ref(false);

const submit = async (form: globalThis.Ref<HTMLFormElement | null>) => {
  try {
    loadingSubmit.value = true;
    const costPool: CostPoolCreateUpdate = {
      cost_center_identifier: costCenterIdentifier.value,
      description: description.value,
      estimated_capacity: Number(estimatedCapacity.value),
      max_capacity: Number(maxCapacity.value),
      name: name.value,
      percentage_of_cost_center: Number(percentageOfCostCenter.value),
      type: type.value,
      unit_description: unitDescription.value,
      unit_type: unitType.value,
      unit_weight_method: unitWeightMethod.value,
    };
    if (!props.isUpdateForm) {
      await costPoolsStore.createCostPool(costPool);
      router.go(-1);
    } else {
      const updateCostPool: CostPoolCreateUpdate = {
        ...costPool,
        id: props.id,
      };
      await costPoolsStore.updateCostPool(updateCostPool);
      router.go(-1);
    }
  } catch (errorMessage: any) {
    serverError.value = true;
    errorMessages.value = new Map();
    form.value?.classList.remove('was-validated');
    if (typeof errorMessage !== 'string' && errorMessage.length > 0) {
      errorMessage.forEach((d: Detail) => errorMessages.value.set(d.loc[1], d.msg));
    }
    form.value?.classList.remove('was-validated');
    console.log(errorMessage);
  } finally {
    loadingSubmit.value = false;
  }
};

const load = async () => {
  if (props.isUpdateForm === true) {
    // Try find the item in store state
    let item = getCostPools.value.find((pt) => pt.id === props.id);
    // If the item is missing, try to fetch it
    if (item === undefined && props.id !== undefined) {
      try {
        const data = await costPoolsStore.readCostPoolById(props.id);
        item = data;
      } catch (error) {
        console.log(error);
      }
    }
    if (item !== undefined) {
      // Set the form data
      costCenterIdentifier.value = item.cost_center_identifier;
      description.value = item.description;
      estimatedCapacity.value =
        item.estimated_capacity !== undefined && item.estimated_capacity !== null
          ? item.estimated_capacity.toString()
          : '';
      maxCapacity.value =
        item.max_capacity !== undefined && item.max_capacity !== null
          ? item.max_capacity.toString()
          : '';
      name.value = item.name;
      percentageOfCostCenter.value =
        item.percentage_of_cost_center !== undefined && item.percentage_of_cost_center !== null
          ? item.percentage_of_cost_center.toString()
          : '';
      type.value = item.type;
      unitDescription.value = item.unit_description ?? '';
      unitType.value =
        item.unit_type !== undefined && item.unit_type !== null ? item.unit_type.toString() : '';
      unitWeightMethod.value = item.unit_weight_method ?? '';
    }
  }
};

onMounted(() => load());
</script>
